import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Mail, MapPin, Send, Loader2, CheckCircle2 } from "lucide-react";
import { BRAND } from "@/data/brand";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Seo } from "@/lib/seo";

export default function Contact() {
  const [params] = useSearchParams();
  const productRef = params.get("product") || "";
  const [form, setForm] = useState({ name: "", email: "", message: productRef ? `Hi! I'm interested in "${productRef}". ` : "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Please enter a valid email.";
    if (!form.message.trim()) e.message = "Please enter a message.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (sending || !validate()) return;
    setSending(true);
    try {
      await api.post("/contact", { name: form.name.trim(), email: form.email.trim(), message: form.message.trim(), product_ref: productRef });
      setSent(true);
      toast.success("Message sent! We'll get back to you soon.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page" className="jm-container py-16">
      <Seo title="Contact" path="/contact" description="Message JohnnyMart to check availability, request more photos, or arrange pickup or shipping for an item." />
      <p className="uppercase tracking-[0.2em] text-brick font-bold text-sm">Contact</p>
      <h1 className="font-display text-4xl sm:text-5xl text-navy mt-2">See something you like?</h1>
      <p className="max-w-2xl mt-4 text-ink/75 leading-relaxed">
        Message JohnnyMart to ask whether an item is still available, request more pictures, or arrange
        pickup or shipping.
      </p>

      <div className="mt-10 grid gap-10 lg:grid-cols-2">
        {sent ? (
          <div data-testid="contact-success" className="bg-white rounded-lg border border-navy/15 p-8 flex flex-col items-center text-center">
            <CheckCircle2 className="w-14 h-14 text-brick" />
            <h2 className="font-display text-2xl text-navy mt-4">Message sent!</h2>
            <p className="text-ink/70 mt-2">Thanks, {form.name}. We'll reply to {form.email} as soon as we can.</p>
            <button onClick={() => { setSent(false); setForm({ name: "", email: "", message: "" }); }} className="mt-5 text-brick font-bold underline">Send another message</button>
          </div>
        ) : (
          <form onSubmit={submit} noValidate className="bg-white rounded-lg border border-navy/15 p-6 sm:p-8">
            {productRef && <p className="mb-4 text-sm bg-cream rounded-md px-3 py-2 text-navy">Asking about: <b>{productRef}</b></p>}
            <label htmlFor="contact-name" className="block text-sm font-bold text-navy uppercase">Name</label>
            <input id="contact-name" data-testid="contact-name" required value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1 w-full rounded-md border border-navy/20 px-4 py-3 outline-none focus:border-brick" />
            {errors.name && <p className="text-brick text-sm mt-1">{errors.name}</p>}

            <label htmlFor="contact-email" className="block text-sm font-bold text-navy uppercase mt-4">Email</label>
            <input id="contact-email" data-testid="contact-email" type="email" required value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-1 w-full rounded-md border border-navy/20 px-4 py-3 outline-none focus:border-brick" />
            {errors.email && <p className="text-brick text-sm mt-1">{errors.email}</p>}

            <label htmlFor="contact-message" className="block text-sm font-bold text-navy uppercase mt-4">Message</label>
            <textarea id="contact-message" data-testid="contact-message" required rows={5} value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="mt-1 w-full rounded-md border border-navy/20 px-4 py-3 outline-none focus:border-brick resize-none" />
            {errors.message && <p className="text-brick text-sm mt-1">{errors.message}</p>}

            <button type="submit" data-testid="contact-submit" disabled={sending}
              className="mt-5 inline-flex items-center gap-2 bg-brick text-white font-bold uppercase rounded-md px-6 py-3.5 hover:bg-navy transition-colors disabled:opacity-60">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sending ? "Sending..." : "Send Message"}
            </button>
          </form>
        )}

        <div className="space-y-4">
          <a href={`mailto:${BRAND.email}`} data-testid="contact-email-link" className="flex items-center gap-4 bg-cream border border-navy/15 rounded-lg p-5 hover:border-brick transition-colors">
            <Mail className="w-6 h-6 text-brick" />
            <div>
              <div className="font-bold text-navy">Email JohnnyMart</div>
              <div className="text-sm text-ink/60 break-all">{BRAND.email}</div>
            </div>
          </a>
          <div className="flex items-center gap-4 bg-cream border border-navy/15 rounded-lg p-5">
            <MapPin className="w-6 h-6 text-brick" />
            <div>
              <div className="font-bold text-navy">Location</div>
              <div className="text-sm text-ink/60">{BRAND.location}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
